import 'twin.macro'

import CardVerifyIcon from 'assets/icons/CardVerfiyIcon'
import { Button } from 'components/UI/button/button'
import dayjs from 'dayjs'
import { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ClipLoader } from 'react-spinners'
import { useCancelGiftMutation, useVerifyCancelGiftMutation } from 'redux/api/send-gift.api'
import { mobilePhoneSelector } from 'redux/store/slices/application-slice'

import OtpVerify from '../otp-verify'

interface Props {
  uuid: string
  executionDate?: string | null
  onClose: () => void
  onSuccess: () => void
}

const CancelGiftModal: FC<Props> = ({ uuid, executionDate, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const mobilePhone = useSelector(mobilePhoneSelector)

  const [cancelGift, { isLoading: isCancelLoading, isSuccess: isCancelSent, error: cancelGiftError }] =
    useCancelGiftMutation()
  const [verifyCancelGift, { isLoading: isVerifyLoading, isSuccess: isVerifySuccess, error: verifyError }] =
    useVerifyCancelGiftMutation()

  const { control, watch } = useForm({ defaultValues: { code: '' } })
  const code = watch('code')

  useEffect(() => {
    if (isVerifySuccess) onSuccess()
  }, [isVerifySuccess, onSuccess])

  const handleConfirm = () => {
    cancelGift({ uuid })
  }

  const handleVerify = () => {
    verifyCancelGift({ uuid, code })
  }

  // ── Success ───────────────────────────────────────────────────────────────
  if (isVerifySuccess) {
    return (
      <div className='sm:max-w-[422px] w-full flex flex-col items-center justify-center py-4 space-y-4'>
        <CardVerifyIcon />
        <p className='text-[22px] font-semibold text-center'>{t('cancel_gift_success', { ns: 'gift' })}</p>
        <Button variant='gradient' onClick={onClose}>
          {t('close', { ns: 'gift' })}
        </Button>
      </div>
    )
  }

  // ── OTP step ──────────────────────────────────────────────────────────────
  if (isCancelSent) {
    return (
      <div tw='sm:max-w-[422px] w-full py-4 space-y-4'>
        <p tw='text-s18 font-bold'>{t('confirm_cancel', { ns: 'gift' })}</p>
        <OtpVerify
          phone={mobilePhone}
          control={control}
          name='code'
          isOtpValid={code.length === 4}
          isLoading={isVerifyLoading}
          onOtpVerify={handleVerify}
          errorMsg={
            (verifyError as any)?.data?.error?.code === 'INVALID_OTP'
              ? t('error_incorrect_code', { ns: 'gift' })
              : (verifyError as any)?.data?.error?.description
          }
        />
      </div>
    )
  }

  // ── Confirm step ──────────────────────────────────────────────────────────
  return (
    <div tw='sm:max-w-[422px] w-full py-4 space-y-4'>
      <p tw='text-s18 font-bold'>{t('cancel_gift', { ns: 'gift' })}?</p>

      {executionDate && executionDate !== '-' && (
        <p tw='text-s14 text-secondary'>
          {t('pending_gift_info', { ns: 'gift', datetime: dayjs(executionDate).format('DD.MM.YYYY HH:mm') })}
        </p>
      )}

      <div tw='flex gap-3'>
        <button tw='w-full bg-primary py-3 px-4 rounded-s14 text-s14 font-medium' onClick={onClose}>
          {t('cancel_no', { ns: 'gift' })}
        </button>
        <button
          tw='w-full py-3 px-4 rounded-s14 text-s14 font-semibold text-white'
          style={{ background: 'linear-gradient(95deg, #e53935, #c62828)' }}
          onClick={handleConfirm}
          disabled={isCancelLoading}
        >
          {isCancelLoading ? <ClipLoader size={20} color='white' /> : t('cancel_yes', { ns: 'gift' })}
        </button>
      </div>

      {cancelGiftError && (
        <p tw='text-s14 text-red-500 text-center'>
          {(cancelGiftError as any)?.data?.error?.code === 'INVALID_OTP'
            ? t('error_invalid_otp', { ns: 'gift' })
            : (cancelGiftError as any)?.data?.error?.description ?? t('default_error', { ns: 'gift' })}
        </p>
      )}
    </div>
  )
}

export default CancelGiftModal
